import { Breadcrumb } from '@/components/common/Breadcrumb';
import SEO from '@/components/common/SEO';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowLeft,
  Calendar,
  Clock,
  Star,
  ChevronRight,
  ChevronLeft,
  Loader2,
  Play,
  Award,
  Briefcase,
  Building,
  Heart,
  Scale,
  Factory,
  BookOpen,
  Users,
  Radio,
  TrendingUp,
  Layers,
  Filter,
  X,
  CalendarDays,
  RotateCcw,
  Video,
  Search,
} from 'lucide-react';
import { industriesAPI, webinarsAPI, seminarsAPI } from '@/services/api';
import { format, isWithinInterval, isSameDay, startOfMonth, endOfMonth, parseISO } from 'date-fns';
import { WebinarCard } from '@/components/common/WebinarCard';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/common/SearchableSelect';

const ITEMS_PER_PAGE = 20; // 20 items per page as requested

const industryIcons = {
  'Briefcase': Briefcase,
  'Users': Users,
  'Microscope': Award,
  'Heart': Heart,
  'Building': Building,
  'BookOpen': BookOpen,
  'Factory': Factory,
  'HardHat': Building,
  'Scale': Scale,
  'Home': Building,
};

// Webinar type filter options
const WEBINAR_TYPES = [
  { id: 'live', label: 'Live', icon: Radio, color: 'text-red-500 bg-red-50' },
  { id: 'recorded', label: 'Recorded', icon: Play, color: 'text-blue-500 bg-blue-50' },
  { id: 'certified', label: 'Certified', icon: Award, color: 'text-green-500 bg-green-50' },
  { id: 'series', label: 'Series', icon: Layers, color: 'text-purple-500 bg-purple-50' },
];

// Date filter modes
const DATE_FILTER_MODES = [
  { id: 'none', label: 'All Dates' },
  { id: 'specific', label: 'Specific Date' },
  { id: 'month', label: 'Specific Month' },
  { id: 'range', label: 'Date Range' },
];

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export default function IndustryDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [industry, setIndustry] = useState(null);
  const [allIndustries, setAllIndustries] = useState([]);
  const [webinars, setWebinars] = useState([]);
  const [seminars, setSeminars] = useState([]);
  const [allContent, setAllContent] = useState([]);
  const [filteredContent, setFilteredContent] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Total counts from API (for hero section)
  const [totalWebinarCount, setTotalWebinarCount] = useState(0);
  const [totalSeminarCount, setTotalSeminarCount] = useState(0);

  // Filters
  const [selectedIndustry, setSelectedIndustry] = useState(id);
  const [contentType, setContentType] = useState('all'); // 'all', 'webinars', 'seminars'
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [dateFilterMode, setDateFilterMode] = useState('none');
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [search, setSearch] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchAllIndustries();
  }, []);

  useEffect(() => {
    // Set selectedIndustry to current industry slug when industry data is loaded
    if (industry?.slug) {
      setSelectedIndustry(industry.slug);
    } else if (id) {
      setSelectedIndustry(id);
    }
  }, [industry, id]);

  useEffect(() => {
    fetchIndustryData();
  }, [id]);

  useEffect(() => {
    // Merge webinars and seminars into allContent
    const merged = [
      ...webinars.map(w => ({ ...w, contentType: 'webinar' })),
      ...seminars.map(s => ({ ...s, contentType: 'seminar' })),
    ];
    setAllContent(merged);
  }, [webinars, seminars]);

  useEffect(() => {
    applyFilters();
    setCurrentPage(1);
  }, [allContent, contentType, selectedTypes, dateFilterMode, selectedDate, selectedMonth, selectedYear, dateRange, search]);

  const fetchAllIndustries = async () => {
    try {
      // Use flat endpoint which includes both main and sub industries
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/industries/flat`);
      const data = await response.json();
      setAllIndustries(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const fetchIndustryData = async () => {
    setLoading(true);
    try {
      // First get industry details to get its UUID
      const industryRes = await industriesAPI.getById(id);
      const industryData = industryRes.data;
      setIndustry(industryData);
      
      // Use the industry's UUID (id field) for fetching webinars/seminars
      const industryUUID = industryData?.id;
      
      if (industryUUID) {
        // Fetch webinars with pagination - get all pages
        let allWebinars = [];
        let webinarPage = 1;
        let webinarTotal = 0;
        const perPage = 100; // Max allowed by API
        
        // First request to get total count
        const firstWebinarRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/webinars?industry_id=${industryUUID}&page=1&per_page=${perPage}`
        ).then(r => r.json());
        
        webinarTotal = firstWebinarRes?.total || 0;
        setTotalWebinarCount(webinarTotal);
        allWebinars = firstWebinarRes?.items || [];
        
        // Fetch remaining pages if needed
        const totalPages = Math.ceil(webinarTotal / perPage);
        if (totalPages > 1) {
          const pagePromises = [];
          for (let p = 2; p <= totalPages; p++) {
            pagePromises.push(
              fetch(`${process.env.REACT_APP_BACKEND_URL}/api/webinars?industry_id=${industryUUID}&page=${p}&per_page=${perPage}`)
                .then(r => r.json())
            );
          }
          const pageResults = await Promise.all(pagePromises);
          for (const res of pageResults) {
            allWebinars = allWebinars.concat(res?.items || []);
          }
        }
        
        // Fetch seminars (usually fewer, single request)
        const seminarsRes = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/seminars?industry_id=${industryUUID}&limit=500`
        ).then(r => r.json());
        
        const seminarsData = Array.isArray(seminarsRes) ? seminarsRes : (seminarsRes?.items || []);
        setTotalSeminarCount(seminarsData.length);
        
        setWebinars(allWebinars);
        setSeminars(seminarsData);
      } else {
        setWebinars([]);
        setSeminars([]);
        setTotalWebinarCount(0);
        setTotalSeminarCount(0);
      }
    } catch (error) {
      console.error('Error fetching industry:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...allContent];

    // Search filter
    if (search.trim()) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(item => {
        const speakersText = Array.isArray(item.speakers)
          ? item.speakers
              .map(s => `${s.first_name || ''} ${s.last_name || ''}`.trim())
              .join(' ')
              .toLowerCase()
          : '';
        return (
          item.title?.toLowerCase().includes(searchLower) ||
          item.description?.toLowerCase().includes(searchLower) ||
          item.speaker_name?.toLowerCase().includes(searchLower) ||
          speakersText.includes(searchLower) ||
          item.webinar_code?.toLowerCase().includes(searchLower) ||
          item.seminar_code?.toLowerCase().includes(searchLower) ||
          item.code?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Content type filter (webinars vs seminars)
    if (contentType === 'webinars') {
      filtered = filtered.filter(item => item.contentType === 'webinar');
    } else if (contentType === 'seminars') {
      filtered = filtered.filter(item => item.contentType === 'seminar');
    }

    // Type filter
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(item => {
        const type = item.webinar_type || item.seminar_type;
        if (selectedTypes.includes('live') && type === 'live') return true;
        if (selectedTypes.includes('recorded') && (type === 'on_demand' || type === 'recorded')) return true;
        if (selectedTypes.includes('certified') && (item.cpe_credits > 0 || item.has_cpe_credits)) return true;
        if (selectedTypes.includes('series') && (item.is_series || item.is_part_of_series)) return true;
        return false;
      });
    }

    // Date filter
    if (dateFilterMode !== 'none') {
      filtered = filtered.filter(item => {
        if (!item.scheduled_date) return false;
        const itemDate = parseISO(item.scheduled_date);
        
        if (dateFilterMode === 'specific' && selectedDate) {
          return isSameDay(itemDate, selectedDate);
        }
        
        if (dateFilterMode === 'month') {
          const monthStart = startOfMonth(new Date(selectedYear, selectedMonth));
          const monthEnd = endOfMonth(new Date(selectedYear, selectedMonth));
          return isWithinInterval(itemDate, { start: monthStart, end: monthEnd });
        }
        
        if (dateFilterMode === 'range' && dateRange.from && dateRange.to) {
          return isWithinInterval(itemDate, { start: dateRange.from, end: dateRange.to });
        }
        
        return true;
      });
    }

    // Sort by newest first
    filtered.sort((a, b) => new Date(b.scheduled_date || 0) - new Date(a.scheduled_date || 0));

    setFilteredContent(filtered);
  }, [allContent, contentType, selectedTypes, dateFilterMode, selectedDate, selectedMonth, selectedYear, dateRange, search]);

  const toggleType = (typeId) => {
    setSelectedTypes(prev => 
      prev.includes(typeId) 
        ? prev.filter(id => id !== typeId)
        : [...prev, typeId]
    );
  };

  const clearDateFilter = () => {
    setDateFilterMode('none');
    setSelectedDate(null);
    setDateRange({ from: null, to: null });
  };

  const clearAllFilters = () => {
    setContentType('all');
    setSelectedTypes([]);
    setDateFilterMode('none');
    setSelectedDate(null);
    setDateRange({ from: null, to: null });
    setSearch('');
  };

  const getDateFilterLabel = () => {
    if (dateFilterMode === 'specific' && selectedDate) {
      return format(selectedDate, 'MMM dd, yyyy');
    }
    if (dateFilterMode === 'month') {
      return `${MONTHS[selectedMonth]} ${selectedYear}`;
    }
    if (dateFilterMode === 'range' && dateRange.from && dateRange.to) {
      return `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd, yyyy')}`;
    }
    return null;
  };

  const handleIndustryChange = (value) => {
    window.location.href = `/industries/${value}`;
  };

  const hasActiveFilters = contentType !== 'all' || selectedTypes.length > 0 || dateFilterMode !== 'none' || search.trim();
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear + i);
  
  // Pagination
  const totalPages = Math.ceil(filteredContent.length / ITEMS_PER_PAGE);
  const paginatedContent = filteredContent.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    
    const pages = [];
    const showEllipsis = totalPages > 7;
    
    if (showEllipsis) {
      if (currentPage <= 4) {
        for (let i = 1; i <= Math.min(5, totalPages); i++) pages.push(i);
        if (totalPages > 5) {
          pages.push('...');
          pages.push(totalPages);
        }
      } else if (currentPage >= totalPages - 3) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    } else {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    }

    return (
      <div className="flex items-center justify-center gap-2 mt-10">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
          className="rounded-lg"
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        
        {pages.map((page, idx) => (
          page === '...' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-zinc-400">...</span>
          ) : (
            <Button
              key={page}
              variant={currentPage === page ? 'default' : 'outline'}
              size="sm"
              onClick={() => setCurrentPage(page)}
              className={`rounded-lg min-w-[40px] ${currentPage === page ? 'bg-zinc-950 text-white' : ''}`}
            >
              {page}
            </Button>
          )
        ))}
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
          className="rounded-lg"
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-zinc-700" />
      </div>
    );
  }

  if (!industry) {
    return (
      <div className="min-h-screen bg-white pt-24">
        <div className="container-custom py-20 text-center">
          <h1 className="font-heading text-2xl text-zinc-950 mb-4">Industry not found</h1>
          <Link to="/industries">
            <Button variant="outline" className="rounded-xl">Back to Industries</Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = industryIcons[industry.icon] || Briefcase;

  return (
    <div className="min-h-screen bg-transparent" data-testid="industry-detail-page">
      <SEO
        title={`${industry.name} Compliance Webinars & Training`}
        description={(industry.description || `Expert-led ${industry.name} governance, risk & compliance webinars and certifications. Stay audit-ready with practical, up-to-date training.`).slice(0, 160)}
        path={`/industries/${industry.slug || industry.id}`}
      />
      {/* Hero Section — light grid canvas (matches Webinars page) */}
      <section className="hero-canvas border-b border-zinc-200 pt-28 pb-8">
        <div className="container-custom">
          <Breadcrumb lastLabel={industry.name} />

          <div className="flex flex-col md:flex-row md:items-end gap-x-6 gap-y-5">
            <div className="flex items-center gap-4 flex-1 min-w-0">
              <div className="w-14 h-14 rounded-xl bg-zinc-100 border border-zinc-200 flex items-center justify-center shrink-0">
                <IconComponent className="w-7 h-7 text-zinc-700" />
              </div>
              <div className="min-w-0">
                <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-[-0.03em] leading-none text-zinc-950">
                  {industry.name}
                </h1>
                <p className="text-zinc-500 text-sm md:text-base mt-2 max-w-2xl line-clamp-2">
                  {industry.description || `Governance, risk & compliance training for ${industry.name}.`}
                </p>
              </div>
            </div>

            <div className="flex gap-8 shrink-0">
              <div>
                <div className="font-heading text-2xl md:text-3xl font-bold text-zinc-950 leading-none">{totalWebinarCount}</div>
                <div className="mono-label text-[10px] text-zinc-400 mt-1.5">Webinars</div>
              </div>
              <div className="pl-8 border-l border-zinc-200">
                <div className="font-heading text-2xl md:text-3xl font-bold text-zinc-950 leading-none">{totalSeminarCount}</div>
                <div className="mono-label text-[10px] text-zinc-400 mt-1.5">Seminars</div>
              </div>
            </div>
          </div>

          {/* Filters — inside hero (on grid), in sync with Webinars page */}
          <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-zinc-200">
            {/* Search — first */}
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
              <Input
                type="text"
                placeholder="Search webinar title, code, or speaker..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 h-9 text-sm bg-white border-zinc-300 rounded-lg"
                data-testid="industry-search-input"
              />
            </div>

            {/* Industry Dropdown */}
            <SearchableSelect
              options={allIndustries.map((ind) => ({
                value: ind.slug || ind.id || ind._id,
                label: ind.name
              }))}
              value={selectedIndustry || ''}
              onValueChange={handleIndustryChange}
              placeholder={industry?.name || 'Select Industry'}
              searchPlaceholder="Search industries..."
              className="w-56 border-zinc-300"
              dataTestId="industry-dropdown"
              preserveOrder={true}
            />

            {/* Content Type Filter */}
            <Select value={contentType} onValueChange={setContentType}>
              <SelectTrigger className="w-40 border-zinc-300 h-9 text-sm">
                <SelectValue placeholder="Content Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Content</SelectItem>
                <SelectItem value="webinars">Webinars Only</SelectItem>
                <SelectItem value="seminars">Seminars Only</SelectItem>
              </SelectContent>
            </Select>

            {/* Type Filter Dropdown */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 text-sm border-zinc-300 gap-2">
                  <Filter className="w-4 h-4" />
                  Type
                  {selectedTypes.length > 0 && (
                    <Badge className="ml-1 bg-zinc-100 text-zinc-950 text-xs px-1.5">
                      {selectedTypes.length}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-3" align="start">
                <div className="space-y-2">
                  {WEBINAR_TYPES.map((type) => {
                    const Icon = type.icon;
                    const isSelected = selectedTypes.includes(type.id);
                    return (
                      <button
                        key={type.id}
                        onClick={() => toggleType(type.id)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg border transition-all text-sm ${
                          isSelected 
                            ? 'border-zinc-900 bg-zinc-50' 
                            : 'border-zinc-200 hover:border-zinc-300'
                        }`}
                      >
                        <div className={`p-1.5 rounded-lg ${type.color}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <span className={isSelected ? 'text-zinc-950 font-medium' : 'text-zinc-700'}>
                          {type.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>

            {/* Date Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-9 text-sm border-zinc-300 gap-2">
                  <CalendarDays className="w-4 h-4" />
                  Date
                  {dateFilterMode !== 'none' && (
                    <Badge className="ml-1 bg-purple-100 text-purple-700 text-xs px-1.5">
                      {getDateFilterLabel() || '1'}
                    </Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-4" align="start">
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {DATE_FILTER_MODES.map((mode) => (
                      <button
                        key={mode.id}
                        onClick={() => {
                          setDateFilterMode(mode.id);
                          if (mode.id === 'none') {
                            setSelectedDate(null);
                            setDateRange({ from: null, to: null });
                          }
                        }}
                        className={`px-3 py-1.5 text-xs rounded-lg border transition-all ${
                          dateFilterMode === mode.id
                            ? 'border-zinc-900 bg-zinc-50 text-zinc-950'
                            : 'border-zinc-200 hover:border-zinc-300'
                        }`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>

                  {dateFilterMode === 'specific' && (
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      className="rounded-md border border-zinc-200"
                    />
                  )}

                  {dateFilterMode === 'month' && (
                    <div className="space-y-2">
                      <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
                        <SelectTrigger className="w-full h-9 text-sm">
                          <SelectValue placeholder="Month" />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((month, idx) => (
                            <SelectItem key={idx} value={idx.toString()}>{month}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                        <SelectTrigger className="w-full h-9 text-sm">
                          <SelectValue placeholder="Year" />
                        </SelectTrigger>
                        <SelectContent>
                          {yearOptions.map((year) => (
                            <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {dateFilterMode === 'range' && (
                    <CalendarComponent
                      mode="range"
                      selected={dateRange}
                      onSelect={setDateRange}
                      numberOfMonths={1}
                      className="rounded-md border border-zinc-200"
                    />
                  )}
                </div>
              </PopoverContent>
            </Popover>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="h-9 text-zinc-600 hover:text-zinc-900"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Clear
              </Button>
            )}
          </div>

          {/* Active Filters Pills */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-zinc-100">
              <span className="text-xs text-zinc-500">Active:</span>
              {search && (
                <Badge 
                  className="bg-zinc-100 text-zinc-700 hover:bg-zinc-200 cursor-pointer text-xs px-2 py-0.5"
                  onClick={() => setSearch('')}
                >
                  "{search}" <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              {contentType !== 'all' && (
                <Badge 
                  className="bg-blue-100 text-blue-700 hover:bg-blue-200 cursor-pointer text-xs px-2 py-0.5"
                  onClick={() => setContentType('all')}
                >
                  {contentType === 'webinars' ? 'Webinars' : 'Seminars'} <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
              {selectedTypes.map(typeId => {
                const type = WEBINAR_TYPES.find(t => t.id === typeId);
                return (
                  <Badge 
                    key={typeId}
                    className="bg-zinc-100 text-zinc-950 hover:bg-zinc-200 cursor-pointer text-xs px-2 py-0.5"
                    onClick={() => toggleType(typeId)}
                  >
                    {type?.label} <X className="w-3 h-3 ml-1" />
                  </Badge>
                );
              })}
              {dateFilterMode !== 'none' && getDateFilterLabel() && (
                <Badge 
                  className="bg-purple-100 text-purple-700 hover:bg-purple-200 cursor-pointer text-xs px-2 py-0.5"
                  onClick={clearDateFilter}
                >
                  {getDateFilterLabel()} <X className="w-3 h-3 ml-1" />
                </Badge>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Main Content - 3 Column Grid */}
      <section className="py-8">
        <div className="container-custom">
          <div className="max-w-6xl mx-auto">
            {/* Results Header */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-zinc-600 text-sm">
                Showing {paginatedContent.length} of {filteredContent.length} results
              </p>
            </div>

            {/* Content Grid */}
            {paginatedContent.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {paginatedContent.map((item, idx) => (
                  <WebinarCard
                    key={item.id}
                    item={item}
                    type={item.contentType}
                    animationDelay={idx * 0.03}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-white rounded-xl border border-zinc-200">
                <div className="w-16 h-16 mx-auto bg-zinc-100 rounded-full flex items-center justify-center mb-4">
                  <Search className="w-8 h-8 text-zinc-400" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-zinc-950 mb-2">
                  No content found
                </h3>
                <p className="text-zinc-600 mb-4 max-w-md mx-auto text-sm">
                  {hasActiveFilters 
                    ? 'Try adjusting your filters or search terms.'
                    : `No webinars or seminars available for ${industry.name} yet.`}
                </p>
                {hasActiveFilters && (
                  <Button onClick={clearAllFilters} size="sm" className="bg-zinc-950 hover:bg-zinc-800 text-white">
                    Clear Filters
                  </Button>
                )}
              </div>
            )}

            {/* Pagination */}
            {renderPagination()}
          </div>
        </div>
      </section>
    </div>
  );
}
